import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The ONE renderer. Pure, deterministic, no editor logic, SSR-safe (no window/document at module scope,
 * no state, no effects). The storefront renders it with no `renderSection` (bare sections); the builder
 * renders the SAME component with renderMode 'edit' and renderSection={EditableSection}.
 */
import { Fragment } from 'react';
import { getDefaultSectionRegistry } from './default-section-registry.js';
import { hydrateDocument } from '../document/hydrate.js';
import { applyThemeVars } from '../theme/applyThemeVars.js';
import { RenderProvider } from './RenderProvider.js';
import { TextsProvider } from './TextsProvider.js';
import { SectionErrorBoundary } from './SectionErrorBoundary.js';
import { FallbackSection } from './FallbackSection.js';
import { collectIconNames } from './collectIconNames.js';
import { materialSymbolsHrefFor } from '../blocks/_shared/materialSymbols.js';
export function PageRenderer({ document, context, registry = getDefaultSectionRegistry(), renderSection, }) {
    const hydrated = hydrateDocument(document, registry);
    // PERF (live storefront only): ship a tiny Material Symbols `icon_names=` subset of just the glyphs this
    // page renders instead of the full ~456 KB font. The builder ('edit') keeps the full font so every icon
    // resolves while authoring; collecting from `hydrated` matches exactly what the sections paint.
    // Wrapped defensively: this runs BEFORE the per-section error boundaries, so a malformed document must
    // fall back to the full font (no subset) — never crash the whole page render.
    let ctx = context;
    if (context.renderMode === 'live') {
        try {
            // Block-field icons (icon-name fields) + the COD form's per-field icons (feature 0026 — these live in
            // `context.codConfig`, NOT in the document, so `collectIconNames` can't see them). Both feed the subset.
            const codIcons = (context.codConfig?.fields ?? [])
                .map((f) => f.icon)
                .filter((n) => typeof n === 'string' && n.length > 0);
            const iconNames = [...collectIconNames(hydrated, registry), ...codIcons];
            if (iconNames.length > 0) {
                ctx = { ...context, materialSymbolsHref: materialSymbolsHrefFor(iconNames) };
            }
        }
        catch {
            ctx = context;
        }
    }
    return (_jsx(RenderProvider, { value: ctx, children: _jsx(TextsProvider, { locale: ctx.locale, texts: ctx.texts, children: _jsx("div", { "data-aqx-page": "", dir: ctx.dir, style: applyThemeVars(ctx.themeSettings, ctx.locale), children: hydrated.sections.map((section, index) => {
                    if (section.disabled && ctx.renderMode === 'live')
                        return null;
                    const def = registry[section.type];
                    const element = def ? (_jsx(SectionErrorBoundary, { mode: ctx.renderMode, children: _jsx(def.Component, { section: section, context: ctx }) })) : (_jsx(FallbackSection, { type: section.type, mode: ctx.renderMode }));
                    const out = renderSection
                        ? renderSection({ section, index, children: element })
                        : element;
                    return _jsx(Fragment, { children: out }, section.id);
                }) }) }) }));
}
//# sourceMappingURL=PageRenderer.js.map